/**
 * HUD-Teil: die Fähigkeitsplätze.
 *
 * Zwei Dinge, die aus der Rückmeldung folgen (Ueberarbeitung B9/B9a):
 *
 *  - **Langes Drücken schaltet zwischen Auto und Hand.** So behält man schwere
 *    Schläge für den Moment, in dem sie sich lohnen.
 *  - **Fähigkeiten, die den Spieler versetzen, sind markiert.** Sie feuern im
 *    Standardgrad „umsichtig" nie von selbst — das muss man sehen können,
 *    ohne ein Menü zu öffnen.
 */

import type { Schnappschuss } from '@pg/protocol';
import { setzeHandbetrieb } from '../einstellungen.js';
import { el, type HudTeil, type HudUmgebung } from './typen.js';

const LANGDRUCK_MS = 380;

/**
 * Woran erkennen wir, dass sich die Belegung geändert hat? An der Liste der
 * Fähigkeits-IDs. Ohne diesen Vergleich erschien eine neu gelernte Fähigkeit
 * erst nach einem Neustart des Abstiegs — genau der gemeldete Fehler.
 */
let letzteBelegung = '';

export const slots: HudTeil = {
  name: 'slots',

  bauen(u: HudUmgebung, s: Schnappschuss) {
    const behaelter = el<HTMLDivElement>('#slots');
    letzteBelegung = s.spieler.slots.map((x) => x.faehigkeit).join('|');
    behaelter.innerHTML = '';

    s.spieler.slots.forEach((slot, i) => {
      const knopf = document.createElement('button');
      knopf.type = 'button';
      knopf.className = 'slot';
      knopf.dataset['slot'] = String(i);
      knopf.innerHTML =
        `<span class="nr">${i + 1}</span>` +
        `<span class="abklingen"></span>` +
        `<span class="marke" aria-hidden="true"></span>` +
        `<span class="name" lang="de">${slot.name}</span>`;

      let halten: ReturnType<typeof setTimeout> | null = null;
      let langGedrueckt = false;

      const start = (e: PointerEvent): void => {
        e.stopPropagation();
        langGedrueckt = false;
        halten = setTimeout(() => {
          langGedrueckt = true;
          const neu = !knopf.classList.contains('hand');
          u.steuerung.setzeHandbetrieb(i, neu);
          setzeHandbetrieb(slot.faehigkeit, neu);
          knopf.classList.toggle('hand', neu);
          if (navigator.vibrate) navigator.vibrate(12);
        }, LANGDRUCK_MS);
      };
      const ende = (e: PointerEvent): void => {
        e.stopPropagation();
        if (halten) clearTimeout(halten);
        if (!langGedrueckt) u.steuerung.loeseAus(i);
      };

      knopf.addEventListener('pointerdown', start);
      knopf.addEventListener('pointerup', ende);
      knopf.addEventListener('pointercancel', () => halten && clearTimeout(halten));
      knopf.addEventListener('contextmenu', (e) => e.preventDefault());

      behaelter.appendChild(knopf);
    });
  },

  aktualisieren(u: HudUmgebung, s: Schnappschuss) {
    // Neue Fähigkeit gelernt? Dann die Knopfreihe neu bauen — sofort, nicht
    // erst beim nächsten Abstieg.
    const belegung = s.spieler.slots.map((x) => x.faehigkeit).join('|');
    if (belegung !== letzteBelegung) this.bauen?.(u, s);

    const knoepfe = el<HTMLDivElement>('#slots').children;
    s.spieler.slots.forEach((slot, i) => {
      const knopf = knoepfe[i] as HTMLElement | undefined;
      if (!knopf) return;
      const anteil = slot.abklingenMax > 0 ? slot.abklingen / slot.abklingenMax : 0;
      (knopf.querySelector('.abklingen') as HTMLElement).style.transform = `scaleY(${anteil})`;
      knopf.classList.toggle('regel', slot.regelAktiv);
      knopf.classList.toggle('bereit', slot.abklingen <= 0 && slot.bezahlbar);
      knopf.classList.toggle('teuer', !slot.bezahlbar);
      knopf.classList.toggle('hand', slot.handbetrieb);
      knopf.classList.toggle('versetzt', slot.bewegtSpieler);
      const marke = knopf.querySelector('.marke') as HTMLElement;
      marke.textContent = slot.handbetrieb ? 'Hand' : slot.bewegtSpieler ? 'Sprung' : '';
    });
  },
};
